import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IKO } from "@/design-system";
import { CURRENCY_CODES, CURRENCY_SPLIT, eventCurrency } from "@/lib/currency";

// WW-187 · ended in real time (naive stored times are Nairobi); no end = start plus 3 hours.
function eventEnded(ev) {
  const toMs = (s) => (s ? Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(s) ? s : `${s}+03:00`) : NaN);
  const end = toMs(ev?.end_at);
  const last = Number.isFinite(end) ? end : toMs(ev?.start_at) + 3 * 3600 * 1000;
  return Number.isFinite(last) && last < Date.now();
}
import SaveLikeRow from "@/design-system/SaveLikeRow";
import { MapPin, User as UserIcon, Link as LinkIcon } from "lucide-react";
import { trackTicketClick } from "@/lib/analytics";

/**
 * D45 (29 Sep 2026) — Feed cards for the public listing.
 * IA: eyebrow = iko (Arial uppercase, iko colour) · title (Poppins 800 lowercase) ·
 * meta = day date · time · venue (Arial #C9C9DA) · social row = by @organiser +
 * avatar stack + "N going" (or "be the first to go" when zero).
 *
 * Card levels:
 *  - <FeatureCard>   — the single top slot ("our pick"). Wide banner ≥600px.
 *  - <CompactCard>   — the small horizontal card <600px.
 *  - <VerticalCard>  — the ≥1024px vertical card (iko band on top, text below).
 */

const TZ = "Africa/Nairobi";
const META_COLOUR = "var(--ww-text-muted)";
const EYEBROW_COLOUR = "var(--iko-eyebrow)"; // Msg 234 §3 · iko name label — resolves to muted cream on night (#C9C9DA), deep ink on cream (#5C5548) via index.css.
const POLITE_TEXT = "#C29AFF"; // legibility on night for polite titles

export function ikoTextOnNight(k) {
  // Msg 247 · deprecated in favour of the `--iko-title-{iko}` CSS var so
  // cards can swap between bright (night) and deep (cream) automatically.
  // Kept for callers that pass an inline `style.color`. Prefer the var.
  if (k === "polite") return POLITE_TEXT;
  return (IKO[k]?.bright || "#FFFFFF");
}
export function ikoTitleVar(k) {
  return `var(--iko-title-${k || "sherehe"})`;
}
export function ikoLabel(k) { return (IKO[k]?.label || "event"); }

// Wave 15.13a (Vania 5 Oct 2026) · multi-day / multi-week events that have
// already started but not ended must NEVER render their original start as if
// it were upcoming. Render "on now · until <day> <d mon>" instead. The flag
// is a stable helper so other surfaces (event detail, admin desk) can reuse
// the same rule.
export function isOnNow(startIso, endIso, now = new Date()) {
  if (!startIso || !endIso) return false;
  const nowMs = now.getTime();
  const sMs = new Date(startIso).getTime();
  const eMs = new Date(endIso).getTime();
  if (isNaN(sMs) || isNaN(eMs)) return false;
  return sMs < nowMs && eMs >= nowMs;
}

export function metaLine(iso, venue, endIso) {
  try {
    const now = new Date();
    if (isOnNow(iso, endIso, now)) {
      const endD = new Date(endIso);
      // WW-161 fix 2.3 (Vania 13 Oct · D-4 QA) · when the event ends
      // within the SAME NIGHT as its start (i.e. the overnight tail
      // folds back · last session ending at or before 06:00 Nairobi
      // on the day after start), say "until 2:00am" instead of
      // "until wed 14 oct".
      const startD = new Date(iso);
      const nairobiYMD = (d) => {
        const parts = new Intl.DateTimeFormat("en-GB", {
          timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
        }).formatToParts(d);
        const y = parseInt(parts.find((p) => p.type === "year").value, 10);
        const m = parseInt(parts.find((p) => p.type === "month").value, 10);
        const dd = parseInt(parts.find((p) => p.type === "day").value, 10);
        return Date.UTC(y, m - 1, dd);
      };
      const dayDiff = Math.round((nairobiYMD(endD) - nairobiYMD(startD)) / 86_400_000);
      let eH = parseInt(endD.toLocaleString("en-GB", { timeZone: TZ, hour: "2-digit", hour12: false }), 10);
      if (!Number.isFinite(eH)) eH = 0;
      if (eH === 24) eH = 0;
      let eM = parseInt(endD.toLocaleString("en-GB", { timeZone: TZ, minute: "2-digit" }), 10);
      if (!Number.isFinite(eM)) eM = 0;
      const endMinOfDay = eH * 60 + eM;
      const foldsToSameNight = dayDiff === 1 && endMinOfDay <= 360;
      if (dayDiff === 0 || foldsToSameNight) {
        const eT = endD.toLocaleString("en-GB", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase().replace(" ", "").replace(/^0:(\d{2})(am|pm)$/, "12:$1$2");
        return [`on now · until ${eT}`, (venue || "").toLowerCase()].filter(Boolean).join(" · ");
      }
      const eDay = endD.toLocaleString("en-GB", { timeZone: TZ, weekday: "short" }).toLowerCase();
      const eDm = endD.toLocaleString("en-GB", { timeZone: TZ, day: "numeric", month: "short" }).toLowerCase();
      const bits = [`on now · until ${eDay} ${eDm}`, (venue || "").toLowerCase()].filter(Boolean);
      return bits.join(" · ");
    }
    const d = new Date(iso);
    const day = d.toLocaleString("en-GB", { timeZone: TZ, weekday: "short" }).toLowerCase();
    const dm = d.toLocaleString("en-GB", { timeZone: TZ, day: "numeric", month: "short" }).toLowerCase();
    const h = d.toLocaleString("en-GB", { timeZone: TZ, hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase().replace(" ", "");
    const bits = [`${day} ${dm}`, h, (venue || "").toLowerCase()].filter(Boolean);
    return bits.join(" · ");
  } catch {
    return venue || "";
  }
}

// Wave 3 #7 · prefer the next-upcoming slot start when the event runs across
// multiple slots. Falls back to the top-level start_at for single-slot events.
export function nextStartAt(ev) {
  return ev?.next_slot?.start_at || ev?.start_at || "";
}

// Wave 3 #7 · "+N more dates" chip data for the card. Returns null when the
// event has a single slot; otherwise the extra-slot count.
export function extraSlotCount(ev) {
  const list = ev?.slots_view || [];
  if (!ev?.has_multiple_slots || list.length < 2) return 0;
  const nowIso = new Date().toISOString();
  const upcoming = list.filter((s) => (s.end_at || s.start_at || "") >= nowIso);
  return Math.max(0, upcoming.length - 1);
}

function MoreDatesChip({ ev }) {
  const n = extraSlotCount(ev);
  if (!n) return null;
  return (
    <span data-testid={`card-more-dates-${ev.id}`}
          style={{
            display: "inline-block", padding: "2px 8px", borderRadius: 999,
            background: "var(--glass-bg)", color: "var(--ww-text)",
            border: "1px solid var(--glass-border)",
            fontFamily: "Arial", fontSize: 10, letterSpacing: "0.08em",
            textTransform: "lowercase", whiteSpace: "nowrap",
          }}>
      +{n} more date{n === 1 ? "" : "s"}
    </span>
  );
}

function goingLine(n, ev) {
  // W-C.34 round 2 (Vania 7 Oct) · "be the first to go" only shows on
  // UPCOMING events with no going count. Past events (end_at or start_at
  // in the past) and awarded events (has member_score/roses) suppress it.
  const ended = eventEnded(ev);
  const awarded = typeof ev?.member_score === "number" && ev.member_score > 0;
  if (ended || awarded) {
    if (!n || n <= 0) return { text: "", muted: true };
    return { text: n === 1 ? "1 went" : `${n} went`, muted: false };
  }
  if (!n || n <= 0) return { text: "be the first to go", muted: true };
  const s = n === 1 ? "1 going" : `${n} going`;
  return { text: s, muted: false };
}

function Avatars({ n }) {
  // No user profile photos yet; render up to 3 iko-colour swatch dots to hint
  // social. Msg 234 §4 · never uses Kili Yellow (reserved for score / CTAs).
  const count = Math.min(3, Math.max(0, n || 0));
  if (!count) return null;
  const palette = ["#FF6B9D", "#9D4EDD", "#44DD88"]; // sherehe · polite · kazi (no yellow, no ambiguity)
  return (
    <div className="flex items-center" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} style={{
          display: "inline-block", width: 20, height: 20, borderRadius: 999,
          background: palette[i % palette.length], marginLeft: i === 0 ? 0 : -6,
          border: "2px solid #20203A",
        }} />
      ))}
    </div>
  );
}

function PosterButton({ ev, w, h, onOpenPoster, testId }) {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  const open = (e) => {
    e.preventDefault(); e.stopPropagation();
    onOpenPoster && onOpenPoster({ src: ev.cover_image_url, alt: ev.title || "", returnEl: ref.current });
  };
  // WW-147 · a cover that will not load falls back to the no poster card.
  if (!ev.cover_image_url || failed) {
    // 12.19b (Vania 1 Oct) — give the fallback a readable token colour on both grounds.
    return (
      <div style={{ width: w, height: h, background: "var(--ww-sunken)", borderRadius: 5,
                    display: "grid", placeItems: "center", color: "var(--ww-text-muted)",
                    border: "1px solid var(--ww-line)",
                    fontFamily: "Poppins", fontWeight: 600, fontSize: 11, textTransform: "lowercase" }}>
        no poster
      </div>
    );
  }
  return (
    <span
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(e); } }}
      aria-label="open poster"
      data-testid={testId}
      style={{ background: "transparent", padding: 0, border: 0, cursor: "zoom-in", display: "inline-block" }}>
      <img src={ev.cover_image_url} alt={ev.title || ""} onError={() => setFailed(true)} style={{
        width: w, height: h, objectFit: "contain", borderRadius: 5,
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)", background: "var(--ww-ground)",
      }} />
    </span>
  );
}

export function ikoPanelBg(iko) {
  // Wave 7 · resolves via CSS var so it swaps by theme:
  //  · dark → full iko hex
  //  · cream → 22% iko tint via color-mix
  const map = {
    sherehe: "var(--iko-panel-sherehe)",
    polite:  "var(--iko-panel-polite)",
    michezo: "var(--iko-panel-michezo)",
    kazi:    "var(--iko-panel-kazi)",
  };
  return map[iko] || "var(--ww-line)";
}
function IkoPosterPanel({ ev, w, h, onOpenPoster, panelStyle }) {
  const [failed, setFailed] = useState(false);
  const openPoster = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (ev.cover_image_url) onOpenPoster && onOpenPoster({ src: ev.cover_image_url, alt: ev.title || "", returnEl: e.currentTarget });
  };
  // Wave 9r (Vania 1 Oct) · reverted: poster stays SMALL (fixed w×h) with
  // breathing room around it. No fillImg mode. Clickable zone is the inner
  // <img> only — the save pin lives outside the panel in the text side.
  return (
    <div
      data-testid={`poster-panel-${ev.id}`}
      style={{ background: ikoPanelBg(ev.iko), display: "grid", placeItems: "center",
               position: "relative",
               ...(panelStyle || {}) }}>
      {ev.cover_image_url && !failed ? (
        <span role="button" tabIndex={0} aria-label="open poster"
              data-testid={`poster-open-${ev.id}`}
              onClick={openPoster}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPoster(e); } }}
              style={{ background: "transparent", border: 0, padding: 0,
                       cursor: "zoom-in", display: "inline-block" }}>
          <img src={ev.cover_image_url} alt={ev.title || ""} onError={() => setFailed(true)}
               style={{ width: w, height: h, objectFit: "contain", borderRadius: 5,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)", background: "var(--ww-ground)" }} />
        </span>
      ) : (
        // 12.19b (Vania 1 Oct) — readable fallback on the iko panel, both grounds.
        <div style={{ width: w, height: h, background: "var(--ww-sunken)", borderRadius: 5,
                      display: "grid", placeItems: "center", color: "var(--ww-text-muted)",
                      border: "1px solid var(--ww-line)",
                      fontFamily: "Poppins", fontWeight: 600, fontSize: 11, textTransform: "lowercase" }}>
          no poster
        </div>
      )}
    </div>
  );
}

function SocialRow({ ev, handle, going }) {
  // Wave 12.5 · hide the line entirely when we have no organiser handle.
  // Previously printed "curator tbc" in italics which Vania flagged as noise.
  if (!handle) return null;
  const g = goingLine(going);
  const hstr = `@${handle}`;
  return (
    <div className="mt-2 flex items-center justify-between" style={{ minHeight: 24, gap: 8 }}>
      <Link
        to={`/app/organiser/${handle}`}
        data-testid={`card-org-${ev.id}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: "Arial", fontSize: 12, color: META_COLOUR,
          textDecoration: "none", padding: "10px 4px", minHeight: 44,
          display: "inline-flex", alignItems: "center", flexShrink: 1, minWidth: 0,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
        by <span style={{ color: "var(--ww-text)", marginLeft: 4 }}>{hstr}</span>
      </Link>
      <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
        <Avatars n={going} />
        <span data-testid={`card-going-${ev.id}`}
              style={{ fontFamily: "Arial", fontSize: 12, color: g.muted ? META_COLOUR : "var(--ww-text)" }}>
          {g.text}
        </span>
      </div>
    </div>
  );
}

// WW-205 · face stack + "N going" on the price row · one tap opens who's going.
function CardGoing({ ev, g, n, preview }) {
  const nav = useNavigate();
  if (!g.text || g.muted || ev._hide_going) return null;
  const palette = ["#FFD60A", "#FF6B9D", "#9D4EDD"];
  const open = (e) => { e.stopPropagation(); if (!preview) nav(`/app/event/${ev.id}#going`); };
  return (
    <button type="button" onClick={open} data-testid={`card-going-${ev.id}`} aria-label={`${g.text} · see who's going`}
            style={{ marginLeft: "auto", minHeight: 32, display: "inline-flex", alignItems: "center", gap: 6,
                     background: "transparent", border: 0, padding: "0 2px", cursor: preview ? "default" : "pointer", flexShrink: 0 }}>
      <span style={{ display: "inline-flex", alignItems: "center" }} aria-hidden>
        {Array.from({ length: Math.min(3, n) }).map((_, i) => (
          <span key={i} style={{ display: "inline-block", width: 22, height: 22, borderRadius: 999,
                                 background: palette[i], border: "2px solid var(--ww-surface)",
                                 marginLeft: i === 0 ? 0 : -8 }} />
        ))}
      </span>
      <span style={{ fontFamily: "Arial", fontSize: 12, color: "var(--ww-text)", whiteSpace: "nowrap" }}>{g.text}</span>
    </button>
  );
}

const CARD_ROW = { fontFamily: "Arial", fontSize: 13, lineHeight: 1.3, color: META_COLOUR, textDecoration: "none",
                   minHeight: 28, display: "flex", alignItems: "center", gap: 6, minWidth: 0, width: "100%" };

function CardVenueRow({ ev, preview }) {
  if (!ev.venue_display) return null;
  const text = (
    <>
      <MapPin size={14} style={{ color: "var(--ww-text-muted)", flexShrink: 0 }} aria-hidden />
      <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minWidth: 0 }}>
        {ev.venue_display.toLowerCase()}
      </span>
    </>
  );
  // WW-205.a · a venue with no venue page still shows, as plain text.
  if (preview || !ev.venue_id) return <span data-testid={`card-venue-${ev.id}`} style={CARD_ROW}>{text}</span>;
  return (
    <Link to={`/app/venue/${ev.venue_id}`} data-testid={`card-venue-${ev.id}`}
          onClick={(e) => e.stopPropagation()} style={CARD_ROW}>{text}</Link>
  );
}

function CardCuratorRow({ ev, handle, preview }) {
  if (!handle) return null;
  const text = (
    <>
      <UserIcon size={14} style={{ color: "var(--ww-text-muted)", flexShrink: 0 }} aria-hidden />
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>@{handle}</span>
    </>
  );
  return preview ? (
    <span data-testid={`card-org-${ev.id}`} style={CARD_ROW}>{text}</span>
  ) : (
    <Link to={`/app/organiser/${handle}`} data-testid={`card-org-${ev.id}`}
          onClick={(e) => e.stopPropagation()} style={CARD_ROW}>{text}</Link>
  );
}

function VenueTapTarget({ ev }) {
  if (!ev.venue_id && !ev.venue_display) return null;
  const label = (ev.venue_display || "").toLowerCase();
  if (!ev.venue_id) return null; // no page to go to without id
  return (
    <Link
      to={`/app/venue/${ev.venue_id}`}
      data-testid={`card-venue-${ev.id}`}
      onClick={(e) => e.stopPropagation()}
      style={{
        fontFamily: "Arial", fontSize: 13, color: META_COLOUR,
        textDecoration: "underline", textDecorationColor: "rgba(201,201,218,0.6)",
        padding: "10px 2px", minHeight: 44, display: "inline-flex", alignItems: "center",
        // Msg 234 fix 11 · clean ellipsis so venue never runs off the card.
        maxWidth: "100%", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</Link>
  );
}

/** COMPACT: horizontal card for <600px. iko column ~72px · poster ~44×60.
 * Msg 234 §2/§3 · card IA top → bottom = poster, iko name (neutral cream),
 * title (bright iko colour), date + time, "be the first to go" (or "N going"),
 * organiser line. */
export function CompactCard({ event: ev, onOpenPoster, preview = false, slim = false, eyebrow = "", chainName = "", iWent = false }) {
  // W-C.35 (Vania 7 Oct · Claude QA round 3) · `preview=true` renders the card
  // as display only: no navigation on tap, organiser handle as plain text (not
  // a Link), price never a tappable anchor, save button hidden. Used ONLY by
  // GuestIntro's onboarding cards so real feed cards stay untouched.
  const titleColour = ikoTextOnNight(ev.iko);
  const handle = (ev.organiser_handle || "").replace(/^@+/, "");
  const going = ev.going_count || 0;
  const g = goingLine(going, ev);
  const nav = useNavigate();
  return (
    <article data-testid={`card-compact-${ev.id}`}
             onClick={preview ? undefined : () => nav(`/app/event/${ev.id}`)}
             className="glass"
             style={{ position: "relative", display: "flex",
                      borderRadius: 14, overflow: "hidden", minHeight: 100, cursor: preview ? "default" : "pointer" }}>
      <IkoPosterPanel ev={ev} w={44} h={60} onOpenPoster={onOpenPoster}
                      panelStyle={{ width: 72, flexShrink: 0 }} />
      <div data-testid={`card-body-${ev.id}`}
           style={{ flex: 1, padding: 12, color: "inherit", minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div data-testid={`card-head-${ev.id}`} style={{ paddingRight: 72, minWidth: 0 }}>
        <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                    fontSize: 11, lineHeight: 1.2, color: EYEBROW_COLOUR, opacity: 0.72 }}>
          {eyebrow || ikoLabel(ev.iko).toLowerCase()}
        </p>
        <h3 style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.015em",
                     fontSize: 16, lineHeight: 1.15, color: ikoTitleVar(ev.iko), margin: "3px 0 4px",
                     textTransform: "lowercase",
                     display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {(ev.title || "").toLowerCase()}
        </h3>
        <p style={{ fontFamily: "Arial", fontSize: 12, lineHeight: 1.35, color: META_COLOUR,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {metaLine(nextStartAt(ev), slim ? "" : (ev.venue_name || ev.venue || ""), ev.end_at)}
        </p>
        {chainName && ev.chain_id && (
          <Link to={`/app/chain/${ev.chain_id}`} data-testid={`card-part-of-${ev.id}`} onClick={(e) => e.stopPropagation()}
                style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontFamily: "Arial", fontSize: 12, fontWeight: 700,
                         color: ikoTitleVar(ev.iko), textDecoration: "none", minWidth: 0 }}>
            <LinkIcon size={12} strokeWidth={2.2} aria-hidden style={{ flexShrink: 0 }} />
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>part of {chainName.toLowerCase()}</span>
          </Link>
        )}
        </div>
        <div data-testid={`card-price-row-${ev.id}`} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <PriceLine ev={ev} preview={preview} />
          {!slim && <CardGoing ev={ev} g={g} n={going} preview={preview} />}
          {iWent && (
            <Link to={`/app/event/${ev.id}?compose=review`} data-testid={`card-i-went-${ev.id}`} onClick={(e) => e.stopPropagation()}
                  style={{ marginLeft: "auto", minHeight: 32, display: "inline-flex", alignItems: "center", padding: "0 12px", borderRadius: 999,
                           border: "1px solid var(--ww-line-strong)", color: "var(--ww-text)", fontFamily: "Poppins", fontWeight: 700, fontSize: 12, textDecoration: "none" }}>
              i went
            </Link>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <MoreDatesChip ev={ev} />
        </div>
        {!slim && (
          <div className="mt-auto" style={{ paddingTop: 4, minWidth: 0 }}>
            <CardVenueRow ev={ev} preview={preview} />
            <CardCuratorRow ev={ev} handle={handle} preview={preview} />
          </div>
        )}
      </div>
      {!preview && (
        <div style={{ position: "absolute", right: 8, top: 8 }}
             onClick={(e) => e.stopPropagation()}>
          <SaveLikeRow eventId={ev.id} size="sm" testIdPrefix={`card-${ev.id}-`} />
        </div>
      )}
    </article>
  );
}

/** VERTICAL: for ≥1024px. Small poster on a short iko band · save button
 *  lives in the text side (never over the poster · Vania 1 Oct revert). */
export function VerticalCard({ event: ev, onOpenPoster, chainName = "" }) {
  const iko = ikoTextOnNight(ev.iko);
  const handle = (ev.organiser_handle || "").replace(/^@+/, "");
  const going = ev.going_count || 0;
  const g = goingLine(going, ev);
  const nav = useNavigate();
  return (
    <article data-testid={`card-vertical-${ev.id}`}
             onClick={() => nav(`/app/event/${ev.id}`)}
             className="glass"
             style={{ position: "relative",
                      borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", height: "100%", cursor: "pointer" }}>
      <IkoPosterPanel ev={ev} w={54} h={72} onOpenPoster={onOpenPoster}
                      panelStyle={{ height: 90, width: "100%" }} />
      <div data-testid={`card-body-${ev.id}`}
           style={{ padding: 14, paddingRight: 56, color: "inherit",
                    display: "flex", flexDirection: "column", flex: 1, position: "relative" }}>
        <div style={{ position: "absolute", right: 10, top: 10 }}
             onClick={(e) => e.stopPropagation()}>
          <SaveLikeRow eventId={ev.id} size="sm" testIdPrefix={`card-${ev.id}-`} />
        </div>
        <p style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                    fontSize: 11, lineHeight: 1.2, color: EYEBROW_COLOUR, opacity: 0.72 }}>
          {ikoLabel(ev.iko).toLowerCase()}
        </p>
        <h3 style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.015em",
                     fontSize: 18, lineHeight: 1.15, color: ikoTitleVar(ev.iko), margin: "4px 0 6px",
                     textTransform: "lowercase",
                     display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {(ev.title || "").toLowerCase()}
        </h3>
        <p style={{ fontFamily: "Arial", fontSize: 13, lineHeight: 1.35, color: META_COLOUR }}>
          {metaLine(nextStartAt(ev), "", ev.end_at)}
        </p>
        {chainName && ev.chain_id && (
          <Link to={`/app/chain/${ev.chain_id}`} data-testid={`card-part-of-${ev.id}`} onClick={(e) => e.stopPropagation()}
                style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontFamily: "Arial", fontSize: 12, fontWeight: 700,
                         color: ikoTitleVar(ev.iko), textDecoration: "none", minWidth: 0 }}>
            <LinkIcon size={12} strokeWidth={2.2} aria-hidden style={{ flexShrink: 0 }} />
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>part of {chainName.toLowerCase()}</span>
          </Link>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <MoreDatesChip ev={ev} />
        </div>
        {/* WW-205.b · same rows as the phone card (option 2). */}
        <div data-testid={`card-price-row-${ev.id}`} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <PriceLine ev={ev} />
          <CardGoing ev={ev} g={g} n={going} />
        </div>
        <div style={{ paddingTop: 4, minWidth: 0, marginRight: -42 }}>
          <CardVenueRow ev={ev} />
          <CardCuratorRow ev={ev} handle={handle} />
        </div>
      </div>
    </article>
  );
}

/** FEATURE (our pick): banner ≥600px, tall card at <600px.
 * Msg 234 §2/§3 · IA top → bottom = poster panel, iko name (neutral cream),
 * title (iko-coloured), date + time, "be the first to go" + organiser.
 * `pillLabel` overrides the default "our pick" wording (D46 §A). */
// WW-212 · the existing WW-173 B "part of <chain>" line, for the pick card.
export function PartOfLine({ ev, chainName }) {
  if (!chainName || !ev.chain_id) return null;
  return (
    <Link to={`/app/chain/${ev.chain_id}`} data-testid={`card-part-of-${ev.id}`} onClick={(e) => e.stopPropagation()}
          style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, fontFamily: "Arial", fontSize: 12, fontWeight: 700,
                   color: ikoTitleVar(ev.iko), textDecoration: "none", minWidth: 0 }}>
      <LinkIcon size={12} strokeWidth={2.2} aria-hidden style={{ flexShrink: 0 }} />
      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>part of {chainName.toLowerCase()}</span>
    </Link>
  );
}

export function FeatureCard({ event: ev, onOpenPoster, wide, pillLabel = "our pick", chainName = "" }) {
  const titleColour = ikoTextOnNight(ev.iko);
  const handle = (ev.organiser_handle || "").replace(/^@+/, "");
  const going = ev.going_count || 0;
  const nav = useNavigate();

  if (wide) {
    return (
      <article data-testid={`card-feature-${ev.id}`}
               onClick={() => nav(`/app/event/${ev.id}`)}
               className="glass"
               style={{ position: "relative", display: "flex",
                        borderRadius: 16, overflow: "hidden", minHeight: 168, cursor: "pointer" }}>
        <IkoPosterPanel ev={ev} w={104} h={140} onOpenPoster={onOpenPoster}
                        panelStyle={{ width: 200, flexShrink: 0 }} />
        <div style={{ position: "absolute", right: 12, top: 12 }}
             onClick={(e) => e.stopPropagation()}>
          <SaveLikeRow eventId={ev.id} size="sm" testIdPrefix={`feature-${ev.id}-`} />
        </div>
        <div data-testid={`card-body-${ev.id}`}
             style={{ flex: 1, padding: 20, paddingRight: 120, color: "inherit", display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span data-testid="feature-pill" style={{
              display: "inline-block", padding: "4px 10px", borderRadius: 999,
              background: "var(--ww-text)", color: "var(--ww-ground)",
              fontFamily: "Arial", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 700,
            }}>{pillLabel}</span>
            <span style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                           fontSize: 11, color: EYEBROW_COLOUR, opacity: 0.72 }}>{ikoLabel(ev.iko).toLowerCase()}</span>
          </div>
          <h3 style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.015em",
                       fontSize: 30, lineHeight: 1.15, color: ikoTitleVar(ev.iko), margin: "8px 0 8px",
                       textTransform: "lowercase",
                       display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {(ev.title || "").toLowerCase()}
          </h3>
          <p style={{ fontFamily: "Arial", fontSize: 14, lineHeight: 1.4, color: META_COLOUR }}>
            {metaLine(nextStartAt(ev), "", ev.end_at)}
          </p>
          <PartOfLine ev={ev} chainName={chainName} />
          <PriceLine ev={ev} />
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <MoreDatesChip ev={ev} />
          </div>
          {/* 12.20c (Vania 1 Oct) · footer rail — venue flexes with ellipsis so
              it never clips to a fragment; social row stays its intrinsic size. */}
          <div className="mt-auto flex items-center gap-3" style={{ minWidth: 0 }}>
            <div style={{ flex: "1 1 0", minWidth: 0, overflow: "hidden" }}>
              <VenueTapTarget ev={ev} />
            </div>
            <div style={{ flexShrink: 0 }}>
              <SocialRow ev={ev} handle={handle} going={going} />
            </div>
          </div>
        </div>
      </article>
    );
  }
  return (
    <article data-testid={`card-feature-${ev.id}`}
             onClick={() => nav(`/app/event/${ev.id}`)}
             className="glass"
             style={{ position: "relative", display: "flex",
                      borderRadius: 16, overflow: "hidden", minHeight: 148, cursor: "pointer" }}>
      {/* WW-204.b · below 390 wide the pill drops 1px and tightens its padding so it stays one line. */}
      <style>{".ww-pick-pill{font-size:10px;padding:3px 8px}.ww-pick-short{display:none}@media (max-width:389px){.ww-pick-pill{font-size:9px;padding:3px 5px}}@media (max-width:359px){.ww-pick-full{display:none}.ww-pick-short{display:inline}}"}</style>
      <IkoPosterPanel ev={ev} w={60} h={80} onOpenPoster={onOpenPoster}
                      panelStyle={{ width: 110, flexShrink: 0 }} />
      <div style={{ position: "absolute", right: 8, top: 8 }}
           onClick={(e) => e.stopPropagation()}>
        <SaveLikeRow eventId={ev.id} size="sm" testIdPrefix={`feature-${ev.id}-`} />
      </div>
      <div data-testid={`card-body-${ev.id}`}
           style={{ flex: 1, padding: 14, color: "inherit", display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div data-testid="feature-pill-row" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingRight: 30 }}>
          <span data-testid="feature-pill" className="ww-pick-pill" aria-label={pillLabel} style={{
            display: "inline-block", borderRadius: 999,
            background: "var(--ww-text)", color: "var(--ww-ground)",
            fontFamily: "Arial", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 700,
          }}>
            <span className="sr-only">{pillLabel}</span>
            <span aria-hidden className={PICK_SHORT[pillLabel] ? "ww-pick-full" : undefined}>{pillLabel}</span>
            {PICK_SHORT[pillLabel] && <span aria-hidden className="ww-pick-short">{PICK_SHORT[pillLabel]}</span>}
          </span>
          <span style={{ fontFamily: "Arial", textTransform: "uppercase", letterSpacing: "0.22em",
                         fontSize: 10, color: EYEBROW_COLOUR, opacity: 0.72 }}>{ikoLabel(ev.iko).toLowerCase()}</span>
        </div>
        <h3 style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.015em",
                     fontSize: 20, lineHeight: 1.15, color: ikoTitleVar(ev.iko), margin: "6px 0 4px",
                     textTransform: "lowercase",
                     display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {(ev.title || "").toLowerCase()}
        </h3>
        <p style={{ fontFamily: "Arial", fontSize: 12, lineHeight: 1.4, color: META_COLOUR }}>
          {metaLine(nextStartAt(ev), "", ev.end_at)}
        </p>
        <PartOfLine ev={ev} chainName={chainName} />
        <PriceLine ev={ev} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <MoreDatesChip ev={ev} />
        </div>
        {/* 12.20c (Vania 1 Oct) · phone footer — same ellipsis guard. */}
        <div className="mt-auto flex items-center gap-2" style={{ minWidth: 0 }}>
          <div style={{ flex: "1 1 0", minWidth: 0, overflow: "hidden" }}>
            <VenueTapTarget ev={ev} />
          </div>
          <div style={{ flexShrink: 0 }}>
            <SocialRow ev={ev} handle={handle} going={going} />
          </div>
        </div>
      </div>
    </article>
  );
}

/** Wave 10.1 (D45 · Vania 1 Oct · updated 1 Oct) · price pill under the date
 *  on every event card.
 *  - with ticket_link + parseable price → tappable pill "KES 500 ↗" /
 *    "from KES 500 ↗" / "free · register ↗"; stopPropagation so the card
 *    nav doesn't fire; opens in a new tab; logs `ticket_click` (source=card).
 *  - with ticket_link + no parseable price → tappable pill "tickets ↗".
 *  - without ticket_link → plain text "free" / "KES 500" / "from KES 500".
 *  Pill style = same iko-neutral chip style as `MoreDatesChip` (glass bg,
 *  --ww-text, 4.5:1 both themes, yellow never as text). Min 32px tall.
 *  The `KES` fragment is wrapped in `.ww-currency` so parent lowercase
 *  styling never downcases it back to "kes". */
function PriceLine({ ev, preview = false }) {
  // WW-146.i · a fully funded safari item reads "fully funded"; paid ones keep the price pill.
  const parsed = ev?.safari?.cost === "fully_funded" ? "fully funded" : parsePrice(ev);
  if (!parsed && !ev?.ticket_link) return null;
  const link = ev?.ticket_link || "";
  const label = parsed || (link ? "tickets" : "");
  if (!label) return null;

  // W-C.34 round 2 (Vania 7 Oct) · the price pill never links to tickets on
  // an ENDED event · drops to a plain-text label so you can't try to buy a
  // ticket for a thing that already happened. The event page itself still
  // handles the "this event has passed" block (W-C.12).
  const ended = eventEnded(ev);

  // W-C.35 (Vania 7 Oct · Claude QA round 3) · onboarding preview cards never
  // render the price as an anchor · plain-text only, no ticket click leak.
  // Non-tappable text when preview, no ticket link, OR when the event ended.
  if (preview || !link || ended) {
    return (
      <p data-testid={`card-price-${ev.id}`}
         style={{ fontFamily: "Arial", fontSize: 12, lineHeight: 1.35,
                  color: parsed === "free" ? "var(--iko-title-kazi)" : "var(--ww-text)",
                  fontWeight: parsed === "free" ? 700 : 600,
                  margin: "2px 0 0" }}>
        {renderPriceContent(label)}
      </p>
    );
  }

  // Tappable pill — pill style matches MoreDatesChip (iko-neutral glass)
  const onPillClick = (e) => {
    e.stopPropagation();
    trackTicketClick({
      eventId: ev.id,
      organiserHandle: ev.organiser_handle || "",
      source: "card",
    });
  };
  return (
    <a href={link} target="_blank" rel="noreferrer"
       data-testid={`card-price-${ev.id}`}
       onClick={onPillClick}
       style={{
         display: "inline-flex", alignItems: "center", gap: 4,
         padding: "6px 12px", borderRadius: 999,
         minHeight: 32,
         background: "var(--glass-bg)", color: "var(--ww-text)",
         border: "1px solid var(--glass-border)", textDecoration: "none",
         fontFamily: "Arial", fontSize: 12, fontWeight: 600,
         marginTop: 4, alignSelf: "flex-start",
         whiteSpace: "nowrap",
       }}>
      {renderPriceContent(label)}
    </a>
  );
}

function renderPriceContent(label /* W-C.27 · no arrow */) {
  // Split on "KES" so we can wrap that span with .ww-currency and keep the
  // rest lowercase. Wave C.27 (Vania 7 Oct) · the "↗" arrow is removed app
  // wide · the pill alone remains tappable. The second arg is retained for
  // backwards compatibility with any caller that still passes `withArrow`.
  const parts = label.split(CURRENCY_SPLIT);
  return (
    <>
      {parts.map((p, i) => CURRENCY_CODES.includes(p)
        ? <span key={i} className="ww-currency">{p}</span>
        : <React.Fragment key={i}>{p}</React.Fragment>)}
    </>
  );
}

/** Parse structured price fields into a short display string (or null if
 *  nothing parsable). KES is always upper-case. */
function parsePrice(ev) {
  // WW-153 · "free · register" also fires when the organiser has
  // ticked `registration_required` on the desk (even if ticket_link
  // happens to be stored under a slot or added last-minute).
  if (ev?.is_free) {
    const needsReg = !!(ev?.ticket_link || ev?.registration_required);
    return needsReg ? "free · register" : "free";
  }
  // WW-184 · the event's own currency; a range starting at 0 reads from the lowest non zero price.
  const cur = eventCurrency(ev);
  const lo = Number.isFinite(ev?.price_min) ? ev.price_min : null;
  const hi = Number.isFinite(ev?.price_max) ? ev.price_max : null;
  const fmt = (n) => `${cur} ${n.toLocaleString("en-US")}`;
  if (lo === 0 && hi != null && hi > 0) {
    const nonZero = (String(ev?.price || "").match(/\d[\d,]*/g) || [])
      .map((s) => Number(s.replace(/,/g, ""))).filter((n) => n > 0);
    return `from ${fmt(nonZero.length ? Math.min(...nonZero) : hi)}`;
  }
  if (lo != null && hi != null && hi > lo) return `from ${fmt(lo)}`;
  if (lo != null) return fmt(lo);
  if (hi != null) return fmt(hi);
  return null;
}

export function formatPriceLine(ev) {
  // Kept for callers that render the price as a plain string (e.g. OurPickCard
  // meta line). Returns a string with uppercase "KES". The .ww-currency wrap
  // only matters inside lowercase-styled containers; this helper's callers
  // render inside non-lowercased <p> elements.
  const p = parsePrice(ev);
  if (p) return p;
  if (ev?.price_note || ev?.price) return "price on the ticket page";
  return "";
}

/** IKO chip row (shared with Discover). Wave 9 · desktop wraps · Wave 12.6
 *  phone single-row horizontal scroll (never wraps on phone). */
export function IkoChipRow({ active, onSelect, keys, label = { all: "all" } }) {
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const style = isDesktop
    ? {}
    : { display: "flex", flexWrap: "nowrap", gap: 6, alignItems: "center",
        overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" };
  return (
    <div className={isDesktop ? "ww-desktop-chip-row" : ""}
         style={style} data-testid="iko-chip-row">
      <Chip iko="all" active={active === "all"} onClick={() => onSelect("all")} label={label.all} />
      {keys.map((k) => (
        <Chip key={k} iko={k} active={active === k} onClick={() => onSelect(active === k ? "all" : k)} />
      ))}
    </div>
  );
}

function Chip({ iko, active, onClick, label }) {
  const t = IKO[iko];
  // Wave 14.3 (Vania 2 Oct) · selected pills use the iko's DEEP ramp as the
  // fill with white text — contrast ≈ 5:1 on all four. Bright + white failed
  // AA (1.76–3.09:1). `all` keeps the brand yellow fill with ink text.
  const bg = active
    ? (iko === "all" ? "#FFD60A" : (t?.deep || "#FFD60A"))
    : "transparent";
  const fg = active
    ? (iko === "all" ? "#1A1A30" : "#FFFFFF")
    : (iko === "all" ? "var(--ww-text)" : ikoTitleVar(iko));
  const border = active ? bg : (iko === "all" ? "#FFD60A" : ikoTitleVar(iko));  // WW-146.b
  // Wave 14.5 (Vania 2 Oct) · pills ~22% smaller. Visible 28×auto, font 13,
  // side padding 12. Hit area kept at ≥40px tall via a transparent button
  // wrapper (the extra 12px is invisible padding above + below the pill).
  // Same shape on For You and Discover (shared `IkoChipRow`).
  return (
    <button data-testid={`feed-iko-${iko}`} onClick={onClick}
      className="whitespace-nowrap lowercase"
      style={{
        background: "transparent", border: 0, padding: "6px 0", margin: 0,
        cursor: "pointer", minHeight: 40,
        display: "inline-flex", alignItems: "center",
        flex: "0 0 auto",
      }}>
      <span style={{
        background: bg, color: fg, border: `1px solid ${border}`,
        fontFamily: "Poppins", fontWeight: 600, fontSize: 13,
        padding: "0 12px", height: 28, minWidth: 40, borderRadius: 999,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        {iko === "all" ? (label || "all") : t?.label?.toLowerCase()}
      </span>
    </button>
  );
}

/** Poster viewer overlay (Esc/tap/swipe-down close). */
export function PosterViewer({ viewer, onClose }) {
  const overlayRef = useRef(null);
  const closeAndReturn = React.useCallback(() => {
    const el = viewer?.returnEl;
    onClose();
    if (el && typeof el.focus === "function") setTimeout(() => el.focus(), 0);
  }, [viewer, onClose]);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); closeAndReturn(); } };
    window.addEventListener("keydown", onKey);
    let startY = null;
    const onTouchStart = (e) => { startY = e.touches?.[0]?.clientY ?? null; };
    const onTouchEnd = (e) => {
      const endY = e.changedTouches?.[0]?.clientY ?? null;
      if (startY != null && endY != null && endY - startY > 80) closeAndReturn();
    };
    const el = overlayRef.current;
    el?.addEventListener("touchstart", onTouchStart);
    el?.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("keydown", onKey);
      el?.removeEventListener("touchstart", onTouchStart);
      el?.removeEventListener("touchend", onTouchEnd);
    };
  }, [closeAndReturn]);
  return (
    <div ref={overlayRef} data-testid="poster-viewer"
         onClick={(e) => { if (e.target === overlayRef.current) closeAndReturn(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(18,18,42,0.92)",
                  display: "grid", placeItems: "center", zIndex: 100 }}>
      <img src={viewer.src} alt={viewer.alt} style={{ maxWidth: "94vw", maxHeight: "94dvh", objectFit: "contain" }} />
      <button data-testid="poster-viewer-close" aria-label="close poster" onClick={closeAndReturn}
              style={{ position: "absolute", top: 16, right: 16, width: 44, height: 44, borderRadius: 999,
                       background: "rgba(255,255,255,0.14)", color: "var(--ww-text)", border: "1px solid rgba(255,255,255,0.32)",
                       fontFamily: "Poppins", fontWeight: 600, fontSize: 20, cursor: "pointer" }}>×</button>
    </div>
  );
}

/** Hook to switch layout at 600/1024/1440. */
export function useWidthBand() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
  React.useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const band = w < 600 ? "s" : w < 1024 ? "m" : w < 1440 ? "l" : "xl";
  const cols = band === "s" ? 1 : band === "m" ? 2 : band === "l" ? 3 : 4;
  const wide = w >= 600;
  return { w, band, cols, wide };
}

/** Time sections config — Founder D45 §1c + D46 Addendum 3 §A.
 * Empty sections are hidden. Keep names in ONE config list so they can rename later.
 * Order matters: this is the render order in For You. */
export const TIME_SECTIONS = [
  { key: "today",   label: "today" },
  { key: "weekend", label: "this weekend" },
  { key: "week",    label: "this week" },
  { key: "later",   label: "later" },
];

const TZ2 = TZ;
function ymd(d) { return d.toLocaleString("en-CA", { timeZone: TZ2, year: "numeric", month: "2-digit", day: "2-digit" }); }
function isoDay(iso) { try { return ymd(new Date(iso)); } catch { return ""; } }
function nairobiWeekday(d) { return d.toLocaleString("en-GB", { timeZone: TZ2, weekday: "short" }); }
function nairobiHour(d) { return parseInt(d.toLocaleString("en-GB", { timeZone: TZ2, hour: "2-digit", hour12: false }), 10); }

/** D46 R2 · Vania bug fix (30 Sep 2026): past events NEVER return an upcoming bucket. */
export function bucketEvent(iso, now = new Date(), endIso = null) {
  if (!iso) return null;
  const d = new Date(iso);
  // Multi-day: if end is in the future keep it visible; otherwise past.
  const endD = endIso ? new Date(endIso) : null;
  const stillOn = endD && endD.getTime() >= now.getTime();
  if (d.getTime() < now.getTime() && !stillOn) return null;

  const k = ymd(d);
  const today = ymd(now);
  if (k === today || (stillOn && ymd(endD) >= today && d.getTime() < now.getTime())) return "today";

  const wd = nairobiWeekday(d);
  const hr = nairobiHour(d);
  const daysAhead = Math.floor((d.getTime() - now.getTime()) / 864e5);
  if (daysAhead <= 7 && daysAhead >= 0) {
    if (wd === "Sat" || wd === "Sun") return "weekend";
    if (wd === "Fri" && hr >= 17) return "weekend";
  }

  const weekKeys = new Set();
  for (let i = 1; i <= 7; i++) weekKeys.add(ymd(new Date(now.getTime() + i * 864e5)));
  if (weekKeys.has(k)) return "week";
  return "later";
}

/** D46 Addendum 3 §A · label for the featured pick pill.
 * Follows the pick's day rather than always saying "our pick". */
// WW-204.c · short pill words below 360 wide only (screen readers get the full label).
const PICK_SHORT = { "this weekend's pick": "weekend pick", "tomorrow's pick": "our pick" };

export function pickPillLabel(iso, now = new Date()) {
  if (!iso) return "our pick";
  const b = bucketEvent(iso, now);
  if (b === "today") return "today's pick";
  const d = new Date(iso);
  const tomorrow = new Date(now.getTime() + 864e5);
  if (ymd(d) === ymd(tomorrow)) return "tomorrow's pick";
  if (b === "weekend") return "this weekend's pick";
  return "our pick";
}

/** D46 Addendum 3 §B · Scrim hero. Wave 7 · night uses the ink overlay,
 * cream uses a soft warm overlay so the hero never shows navy under cream.
 * An optional `iko` mixes a 22% iko tint on cream to match the poster panel. */
export function ScrimHero({ image, height = 300, children, testId, iko }) {
  const nightOverlay = "rgba(26,26,48,0.90)";
  const creamOverlay = iko
    ? `color-mix(in srgb, var(--iko-panel-${iko}) 90%, transparent)`
    : "rgba(251,244,233,0.86)";
  return (
    <div data-testid={testId}
         style={{ position: "relative", width: "100%", height, overflow: "hidden",
                  background: "var(--ww-sunken)" }}>
      {image && (
        <img src={image} alt="" aria-hidden style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          objectFit: "cover", filter: "blur(28px) saturate(1.05)", transform: "scale(1.18)",
        }} />
      )}
      <div aria-hidden style={{ position: "absolute", inset: 0,
                                 background: "var(--scrim-hero-overlay)" }} />
      <style>{`
        :root { --scrim-hero-overlay: ${nightOverlay}; }
        html[data-ww-theme="cream"] { --scrim-hero-overlay: ${creamOverlay}; }
      `}</style>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>{children}</div>
    </div>
  );
}

/** Circle button that sits on top of the scrim hero. #20203A, 1px #2F2F52 edge. */
export function ScrimButton({ children, onClick, ariaLabel, testId, style }) {
  return (
    <button aria-label={ariaLabel} data-testid={testId} onClick={onClick}
            style={{
              width: 40, height: 40, borderRadius: 999,
              background: "var(--ww-surface)", border: "1px solid #2F2F52",
              color: "var(--ww-text)", display: "grid", placeItems: "center",
              cursor: "pointer", ...style,
            }}>
      {children}
    </button>
  );
}
